import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { JAZYKY, dejJazyk, prelozit, ulozJazyk, vychoziJazyk, zJazyka } from '../lib/i18n';

/**
 * Zvolený jazyk pro Bloom World.
 *
 * Volba se drží jen v prohlížeči, ne na serveru. Je to údaj o člověku — jazyk
 * napovídá, odkud je — a aplikace, která slibuje, že data nesbírá, ho nemá
 * proč mít uložený u účtu. Na jiném zařízení se prostě určí znovu podle
 * prohlížeče.
 */
const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [jazyk, setJazyk] = useState(vychoziJazyk);

  // Chybové hlášky píše server. Aby chodily ve stejném jazyce jako zbytek
  // rozhraní, jede volba s každým požadavkem v hlavičce. Server ji použije
  // jen na výběr slov a zahodí – neukládá se ani nezapisuje do logu.
  useEffect(() => {
    axios.defaults.headers.common['X-Bloom-Jazyk'] = jazyk;
    document.documentElement.lang = jazyk;
  }, [jazyk]);

  const prepni = useCallback((kod) => {
    setJazyk(kod);
    ulozJazyk(kod);
  }, []);

  const hodnota = useMemo(() => ({
    jazyk,
    jazyky: JAZYKY,
    // Jestli překlad četl někdo, kdo tím jazykem mluví. Patička to přiznává.
    overeno: dejJazyk(jazyk).overeno,
    prepni,
    t: (klic, parametry) => prelozit(jazyk, klic, parametry),
    // Pro texty ze serveru, které mají obě podoby (název země, poznámka u údaje).
    dvojjazycne: (cs, en) => zJazyka(jazyk, cs, en),
    // Datum se v angličtině píše jinak než česky. Ať to nemusí řešit každá
    // stránka zvlášť, formátuje se tady.
    datum: (iso, volby) => {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleDateString(jazyk === 'en' ? 'en-GB' : 'cs-CZ', volby);
    },
    // Datum i čas – u komentářů a zpráv, kde na hodině záleží.
    datumCas: (iso, volby) => {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleString(jazyk === 'en' ? 'en-GB' : 'cs-CZ', volby);
    },
  }), [jazyk, prepni]);

  return (
    <LanguageContext.Provider value={hodnota}>{children}</LanguageContext.Provider>
  );
}

export function useJazyk() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useJazyk musí být uvnitř LanguageProvider');
  return ctx;
}
